'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import type { StyleWithCssVariables } from '@repo/ui/lib/css-variables';
import { cn } from '@repo/ui/lib/utils';
import { Button } from '@repo/ui/components/ui/button';
import { Input } from '@repo/ui/components/ui/input';
import { Separator } from '@repo/ui/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/components/ui/sheet';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components/ui/tooltip';
import { PanelLeftIcon, XIcon } from 'lucide-react';
import { ContentBreakpointContext, DEFAULT_SIDEBAR, SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON, SIDEBAR_WIDTH_MOBILE, SidebarContext, SidebarInstanceContext } from './context';
import type { SidebarRegistry, SidebarResize, SidebarToken } from './context';
import { useElementBreakpoint } from './hooks/use-content-breakpoint';
import { useSidebar } from './hooks/use-sidebar';
import { useSidebarProviderState } from './hooks/use-sidebar-provider-state';
import { useSidebarResize } from './hooks/use-sidebar-resize';
import type { UseSidebarProviderStateOptions } from './hooks/use-sidebar-provider-state';
function Sidebar(t0) {
  const $ = _c(94);
  let children;
  let className;
  let dir;
  let props;
  let resize;
  let style;
  let t1;
  let t2;
  let t3;
  let t4;
  let width;
  if ($[0] !== t0) {
    ({
      children,
      className,
      collapsible: t1,
      dir,
      name: t2,
      resize,
      side: t3,
      style,
      variant: t4,
      width,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = dir;
    $[4] = props;
    $[5] = resize;
    $[6] = style;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = width;
  } else {
    children = $[1];
    className = $[2];
    dir = $[3];
    props = $[4];
    resize = $[5];
    style = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    width = $[11];
  }
  const collapsible = t1 === undefined ? "offcanvas" : t1;
  const name = t2 === undefined ? DEFAULT_SIDEBAR : t2;
  const side = t3 === undefined ? "left" : t3;
  const variant = t4 === undefined ? "sidebar" : t4;
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("Sidebar must be used within a SidebarProvider.");
  }
  let t5;
  if ($[12] !== context || $[13] !== name) {
    t5 = context.getSidebarState(name);
    $[12] = context;
    $[13] = name;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    isMobile,
    openMobile,
    setOpenMobile,
    stackOffset,
    state
  } = t5;
  const resizer = useSidebarResize(side, collapsible !== "none" && !isMobile && state === "expanded" ? resize : undefined);
  let t6;
  if ($[15] !== context || $[16] !== name || $[17] !== side || $[18] !== width) {
    t6 = () => context.registerSidebar(name, {
      side,
      width: width ?? SIDEBAR_WIDTH
    });
    $[15] = context;
    $[16] = name;
    $[17] = side;
    $[18] = width;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== context.registerSidebar || $[21] !== name || $[22] !== side || $[23] !== width) {
    t7 = [context.registerSidebar, name, side, width];
    $[20] = context.registerSidebar;
    $[21] = name;
    $[22] = side;
    $[23] = width;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  React.useEffect(t6, t7);
  let t8;
  if ($[25] !== width) {
    t8 = width ? {
      "--sidebar-width": width
    } : {};
    $[25] = width;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== stackOffset || $[28] !== t8) {
    t9 = {
      "--sidebar-stack-offset": stackOffset,
      ...t8
    };
    $[27] = stackOffset;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const sidebarStyle = t9;
  if (collapsible === "none") {
    let t10;
    if ($[30] !== className) {
      t10 = cn("flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground", className);
      $[30] = className;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t11;
    if ($[32] !== sidebarStyle || $[33] !== style) {
      t11 = {
        ...sidebarStyle,
        ...style
      };
      $[32] = sidebarStyle;
      $[33] = style;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] !== children || $[36] !== name.name || $[37] !== props || $[38] !== t10 || $[39] !== t11) {
      t12 = <div className={t10} data-sidebar-name={name.name} data-slot="sidebar" style={t11} {...props}>{children}</div>;
      $[35] = children;
      $[36] = name.name;
      $[37] = props;
      $[38] = t10;
      $[39] = t11;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    let t13;
    if ($[41] !== name || $[42] !== t12) {
      t13 = <SidebarInstanceContext.Provider value={name}>{t12}</SidebarInstanceContext.Provider>;
      $[41] = name;
      $[42] = t12;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    return t13;
  }
  if (isMobile) {
    const t10 = width ?? SIDEBAR_WIDTH_MOBILE;
    let t11;
    if ($[44] !== style || $[45] !== t10) {
      t11 = {
        "--sidebar-width": t10,
        ...style
      };
      $[44] = style;
      $[45] = t10;
      $[46] = t11;
    } else {
      t11 = $[46];
    }
    const mobileStyle = t11;
    let t12;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SheetHeader className="sr-only"><SheetTitle>Sidebar</SheetTitle><SheetDescription>Displays the mobile sidebar.</SheetDescription></SheetHeader>;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    let t13;
    if ($[48] !== children) {
      t13 = <div className="flex h-full w-full flex-col">{children}</div>;
      $[48] = children;
      $[49] = t13;
    } else {
      t13 = $[49];
    }
    let t14;
    if ($[50] !== name || $[51] !== t13) {
      t14 = <SidebarInstanceContext.Provider value={name}>{t13}</SidebarInstanceContext.Provider>;
      $[50] = name;
      $[51] = t13;
      $[52] = t14;
    } else {
      t14 = $[52];
    }
    let t15;
    if ($[53] !== dir || $[54] !== mobileStyle || $[55] !== name.name || $[56] !== props || $[57] !== side || $[58] !== t14) {
      t15 = <SheetContent className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden" data-mobile="true" data-sidebar="sidebar" data-sidebar-name={name.name} data-slot="sidebar" dir={dir} side={side} style={mobileStyle} {...props}>{t12}{t14}</SheetContent>;
      $[53] = dir;
      $[54] = mobileStyle;
      $[55] = name.name;
      $[56] = props;
      $[57] = side;
      $[58] = t14;
      $[59] = t15;
    } else {
      t15 = $[59];
    }
    let t16;
    if ($[60] !== openMobile || $[61] !== setOpenMobile || $[62] !== t15) {
      t16 = <Sheet onOpenChange={setOpenMobile} open={openMobile}>{t15}</Sheet>;
      $[60] = openMobile;
      $[61] = setOpenMobile;
      $[62] = t15;
      $[63] = t16;
    } else {
      t16 = $[63];
    }
    return t16;
  }
  const t10 = state === "collapsed" ? collapsible : "";
  const t11 = resizer?.resizing;
  const t12 = name.name;
  const t13 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)";
  let t14;
  if ($[64] !== t13) {
    t14 = cn("relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear group-data-[resizing=true]:transition-none", "group-data-[collapsible=offcanvas]:w-0", "group-data-[side=right]:rotate-180", t13);
    $[64] = t13;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t14) {
    t15 = <div className={t14} data-slot="sidebar-gap" />;
    $[66] = t14;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  const t16 = variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l";
  let t17;
  if ($[68] !== className || $[69] !== t16) {
    t17 = cn("fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear group-data-[resizing=true]:transition-none data-[side=left]:left-(--sidebar-stack-offset) data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)] data-[side=right]:right-(--sidebar-stack-offset) data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)] md:flex", t16, className);
    $[68] = className;
    $[69] = t16;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[71] !== children || $[72] !== name) {
    t18 = <div className="flex size-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 group-data-[variant=floating]:ring-sidebar-border" data-sidebar="sidebar" data-slot="sidebar-inner"><SidebarInstanceContext.Provider value={name} data-sentry-element="SidebarInstanceContext.Provider" data-sentry-source-file="sidebar.tsx">{children}</SidebarInstanceContext.Provider></div>;
    $[71] = children;
    $[72] = name;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  let t19;
  if ($[74] !== resizer) {
    t19 = resizer && <div aria-label="Resize sidebar" className="absolute inset-y-0 z-20 w-2 cursor-col-resize touch-none outline-hidden group-data-[side=left]:right-0 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:transition-colors group-data-[resizing=true]:after:bg-sidebar-ring hover:after:bg-sidebar-border focus-visible:after:bg-sidebar-ring" data-slot="sidebar-resize-handle" {...resizer.handleProps} />;
    $[74] = resizer;
    $[75] = t19;
  } else {
    t19 = $[75];
  }
  let t20;
  if ($[76] !== name.name || $[77] !== props || $[78] !== side || $[79] !== style || $[80] !== t17 || $[81] !== t18 || $[82] !== t19) {
    t20 = <div className={t17} data-side={side} data-sidebar-name={name.name} data-slot="sidebar-container" style={style} {...props}>{t18}{t19}</div>;
    $[76] = name.name;
    $[77] = props;
    $[78] = side;
    $[79] = style;
    $[80] = t17;
    $[81] = t18;
    $[82] = t19;
    $[83] = t20;
  } else {
    t20 = $[83];
  }
  let t21;
  if ($[84] !== name.name || $[85] !== side || $[86] !== sidebarStyle || $[87] !== state || $[88] !== t10 || $[89] !== t11 || $[90] !== t15 || $[91] !== t20 || $[92] !== variant) {
    t21 = <div className="group peer hidden text-sidebar-foreground md:block" data-collapsible={t10} data-resizing={t11} data-side={side} data-sidebar-name={t12} data-slot="sidebar" data-state={state} data-variant={variant} style={sidebarStyle} data-sentry-component="Sidebar" data-sentry-source-file="sidebar.tsx">{t15}{t20}</div>;
    $[84] = name.name;
    $[85] = side;
    $[86] = sidebarStyle;
    $[87] = state;
    $[88] = t10;
    $[89] = t11;
    $[90] = t15;
    $[91] = t20;
    $[92] = variant;
    $[93] = t21;
  } else {
    t21 = $[93];
  }
  return t21;
}
function SidebarClose(t0) {
  const $ = _c(20);
  let children;
  let className;
  let onClick;
  let props;
  let target;
  if ($[0] !== t0) {
    ({
      children,
      className,
      onClick,
      target,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClick;
    $[4] = props;
    $[5] = target;
  } else {
    children = $[1];
    className = $[2];
    onClick = $[3];
    props = $[4];
    target = $[5];
  }
  const {
    isMobile,
    setOpen,
    setOpenMobile
  } = useSidebar(target);
  let t1;
  if ($[6] !== className) {
    t1 = cn(className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== isMobile || $[9] !== onClick || $[10] !== setOpen || $[11] !== setOpenMobile) {
    t2 = event => {
      onClick?.(event);
      if (event.defaultPrevented) {
        return;
      }
      if (isMobile) {
        setOpenMobile(false);
      } else {
        setOpen(false);
      }
    };
    $[8] = isMobile;
    $[9] = onClick;
    $[10] = setOpen;
    $[11] = setOpenMobile;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== children) {
    t3 = children ?? <><XIcon /><span className="sr-only">Close Sidebar</span></>;
    $[13] = children;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== props || $[16] !== t1 || $[17] !== t2 || $[18] !== t3) {
    t4 = <Button className={t1} data-sidebar="close" data-slot="sidebar-close" onClick={t2} size="icon-sm" variant="ghost" {...props} data-sentry-element="Button" data-sentry-component="SidebarClose" data-sentry-source-file="sidebar.tsx">{t3}</Button>;
    $[15] = props;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}
function SidebarContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-sidebar="content" data-slot="sidebar-content" {...props} data-sentry-component="SidebarContent" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-sidebar="footer" data-slot="sidebar-footer" {...props} data-sentry-component="SidebarFooter" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex w-full min-w-0 flex-col p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-sidebar="group" data-slot="sidebar-group" {...props} data-sentry-component="SidebarGroup" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroupAction(t0) {
  const $ = _c(10);
  let render;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      render: t4,
      ...props
    } = t0;
    render = t4;
    t3 = useRender;
    t1 = "button";
    t2 = mergeProps({
      className: cn("absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0", className)
    }, props);
    $[0] = t0;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    render = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      sidebar: "group-action",
      slot: "sidebar-group-action"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== render || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[6] = render;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t3(t5);
}
function SidebarGroupContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-sidebar="group-content" data-slot="sidebar-group-content" {...props} data-sentry-component="SidebarGroupContent" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarGroupLabel(t0) {
  const $ = _c(10);
  let render;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      render: t4,
      ...props
    } = t0;
    render = t4;
    t3 = useRender;
    t1 = "div";
    t2 = mergeProps({
      className: cn("flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 ring-sidebar-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", className)
    }, props);
    $[0] = t0;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    render = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      sidebar: "group-label",
      slot: "sidebar-group-label"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== render || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[6] = render;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t3(t5);
}
function SidebarHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-sidebar="header" data-slot="sidebar-header" {...props} data-sentry-component="SidebarHeader" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("h-8 w-full bg-background shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Input className={t1} data-sidebar="input" data-slot="sidebar-input" {...props} data-sentry-element="Input" data-sentry-component="SidebarInput" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarInset(t0) {
  const $ = _c(11);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const [element, setElement] = React.useState(null);
  const contentBreakpoint = useElementBreakpoint(element);
  let t1;
  if ($[3] !== className) {
    t1 = cn("@container/main relative flex w-full min-w-0 flex-1 flex-col bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <main className={t1} data-slot="sidebar-inset" ref={setElement} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== contentBreakpoint || $[9] !== t2) {
    t3 = <ContentBreakpointContext.Provider value={contentBreakpoint} data-sentry-element="ContentBreakpointContext.Provider" data-sentry-component="SidebarInset" data-sentry-source-file="sidebar.tsx">{t2}</ContentBreakpointContext.Provider>;
    $[8] = contentBreakpoint;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function SidebarMenu(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul className={t1} data-sidebar="menu" data-slot="sidebar-menu" {...props} data-sentry-component="SidebarMenu" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li className={t1} data-sidebar="menu-item" data-slot="sidebar-menu-item" {...props} data-sentry-component="SidebarMenuItem" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarProvider(t0) {
  const $ = _c(28);
  let children;
  let className;
  let controlledSidebars;
  let defaultOpen;
  let onOpenChange;
  let open;
  let props;
  let registry;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      className,
      controlledSidebars,
      defaultOpen,
      onOpenChange,
      open,
      registry,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = controlledSidebars;
    $[4] = defaultOpen;
    $[5] = onOpenChange;
    $[6] = open;
    $[7] = props;
    $[8] = registry;
    $[9] = style;
  } else {
    children = $[1];
    className = $[2];
    controlledSidebars = $[3];
    defaultOpen = $[4];
    onOpenChange = $[5];
    open = $[6];
    props = $[7];
    registry = $[8];
    style = $[9];
  }
  let t1;
  if ($[10] !== controlledSidebars || $[11] !== defaultOpen || $[12] !== onOpenChange || $[13] !== open || $[14] !== registry) {
    t1 = {
      controlledSidebars,
      defaultOpen,
      onOpenChange,
      open,
      registry
    };
    $[10] = controlledSidebars;
    $[11] = defaultOpen;
    $[12] = onOpenChange;
    $[13] = open;
    $[14] = registry;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const contextValue = useSidebarProviderState(t1);
  let t2;
  if ($[16] !== style) {
    t2 = {
      "--sidebar-width": SIDEBAR_WIDTH,
      "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
      ...style
    };
    $[16] = style;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  const wrapperStyle = t2;
  let t3;
  if ($[18] !== className) {
    t3 = cn("flex min-h-svh w-full", className);
    $[18] = className;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== children || $[21] !== props || $[22] !== t3 || $[23] !== wrapperStyle) {
    t4 = <div className={t3} data-slot="sidebar-wrapper" style={wrapperStyle} {...props}>{children}</div>;
    $[20] = children;
    $[21] = props;
    $[22] = t3;
    $[23] = wrapperStyle;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== contextValue || $[26] !== t4) {
    t5 = <SidebarContext.Provider value={contextValue} data-sentry-element="SidebarContext.Provider" data-sentry-component="SidebarProvider" data-sentry-source-file="sidebar.tsx">{t4}</SidebarContext.Provider>;
    $[25] = contextValue;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  return t5;
}
function SidebarRail(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    toggleSidebar
  } = useSidebar();
  let t1;
  if ($[3] !== className) {
    t1 = cn("absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2", "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize", "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize", "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar", "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2", "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1 || $[7] !== toggleSidebar) {
    t2 = <button aria-label="Toggle Sidebar" className={t1} data-sidebar="rail" data-slot="sidebar-rail" onClick={toggleSidebar} tabIndex={-1} title="Toggle Sidebar" {...props} data-sentry-component="SidebarRail" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function SidebarSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-2 w-auto bg-sidebar-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Separator className={t1} data-sidebar="separator" data-slot="sidebar-separator" {...props} data-sentry-element="Separator" data-sentry-component="SidebarSeparator" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarTrigger(t0) {
  const $ = _c(19);
  let children;
  let className;
  let onClick;
  let props;
  let target;
  if ($[0] !== t0) {
    ({
      children,
      className,
      onClick,
      target,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClick;
    $[4] = props;
    $[5] = target;
  } else {
    children = $[1];
    className = $[2];
    onClick = $[3];
    props = $[4];
    target = $[5];
  }
  const {
    isMobile,
    open,
    openMobile,
    toggleSidebar
  } = useSidebar(target);
  const t1 = isMobile ? openMobile : open;
  let t2;
  if ($[6] !== className) {
    t2 = cn(className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== onClick || $[9] !== toggleSidebar) {
    t3 = event => {
      onClick?.(event);
      if (event.defaultPrevented) {
        return;
      }
      toggleSidebar();
    };
    $[8] = onClick;
    $[9] = toggleSidebar;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children) {
    t4 = children ?? <><PanelLeftIcon /><span className="sr-only">Toggle Sidebar</span></>;
    $[11] = children;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== props || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <Button aria-expanded={t1} className={t2} data-sidebar="trigger" data-slot="sidebar-trigger" onClick={t3} size="icon-sm" variant="ghost" {...props} data-sentry-element="Button" data-sentry-component="SidebarTrigger" data-sentry-source-file="sidebar.tsx">{t4}</Button>;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
const sidebarMenuButtonVariants = cva('peer/menu-button group/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm ring-sidebar-ring outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-active:bg-sidebar-accent data-active:font-medium data-active:text-sidebar-accent-foreground data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&>span:last-child]:truncate', {
  defaultVariants: {
    size: 'default',
    variant: 'default'
  },
  variants: {
    size: {
      default: 'h-8 text-sm',
      lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      sm: 'h-7 text-xs'
    },
    variant: {
      default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      outline: 'bg-background shadow-[0_0_0_1px_var(--sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_var(--sidebar-accent)]'
    }
  }
});
function SidebarMenuAction(t0) {
  const $ = _c(10);
  let render;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      render: t4,
      showOnHover: t5,
      ...props
    } = t0;
    render = t4;
    const showOnHover = t5 === undefined ? false : t5;
    t3 = useRender;
    t1 = "button";
    t2 = mergeProps({
      className: cn("absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0", showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-active/menu-button:text-sidebar-accent-foreground aria-expanded:opacity-100 md:opacity-0", className)
    }, props);
    $[0] = t0;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    render = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      sidebar: "menu-action",
      slot: "sidebar-menu-action"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== render || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[6] = render;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t3(t5);
}
function SidebarMenuBadge(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-active/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} data-sidebar="menu-badge" data-slot="sidebar-menu-badge" {...props} data-sentry-component="SidebarMenuBadge" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuButton(t0) {
  const $ = _c(18);
  let {
    className,
    isActive: t1,
    render,
    size: t2,
    tooltip,
    variant: t3,
    ...props
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "default" : t2;
  const variant = t3 === undefined ? "default" : t3;
  const {
    isMobile,
    state
  } = useSidebar();
  const t4 = "button";
  const t5 = mergeProps({
    className: cn(sidebarMenuButtonVariants({
      size,
      variant
    }), className)
  }, props);
  let t6;
  if ($[0] !== render || $[1] !== tooltip) {
    t6 = !tooltip ? render : <TooltipTrigger render={render} />;
    $[0] = render;
    $[1] = tooltip;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== isActive || $[4] !== size) {
    t7 = {
      active: isActive,
      sidebar: "menu-button",
      size,
      slot: "sidebar-menu-button"
    };
    $[3] = isActive;
    $[4] = size;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t5 || $[7] !== t6 || $[8] !== t7) {
    t8 = {
      defaultTagName: t4,
      props: t5,
      render: t6,
      state: t7
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const comp = useRender(t8);
  if (!tooltip) {
    return comp;
  }
  if (typeof tooltip === "string") {
    let t9;
    if ($[10] !== tooltip) {
      t9 = {
        children: tooltip
      };
      $[10] = tooltip;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    tooltip = t9;
  }
  const t9 = state !== "collapsed" || isMobile;
  let t10;
  if ($[12] !== t9 || $[13] !== tooltip) {
    t10 = <TooltipContent align="center" hidden={t9} side="right" {...tooltip} data-sentry-element="TooltipContent" data-sentry-source-file="sidebar.tsx" />;
    $[12] = t9;
    $[13] = tooltip;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== comp || $[16] !== t10) {
    t11 = <Tooltip data-sentry-element="Tooltip" data-sentry-component="SidebarMenuButton" data-sentry-source-file="sidebar.tsx">{comp}{t10}</Tooltip>;
    $[15] = comp;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}
function SidebarMenuSkeleton(t0) {
  const $ = _c(17);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showIcon = t1 === undefined ? false : t1;
  const [width] = React.useState(_temp);
  let t2;
  if ($[4] !== width) {
    t2 = {
      "--skeleton-width": width
    };
    $[4] = width;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const skeletonStyle = t2;
  let t3;
  if ($[6] !== className) {
    t3 = cn("flex h-8 items-center gap-2 rounded-md px-2", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== showIcon) {
    t4 = showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />;
    $[8] = showIcon;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== skeletonStyle) {
    t5 = <Skeleton className="h-4 max-w-(--skeleton-width) flex-1" data-sidebar="menu-skeleton-text" style={skeletonStyle} data-sentry-element="Skeleton" data-sentry-source-file="sidebar.tsx" />;
    $[10] = skeletonStyle;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className={t3} data-sidebar="menu-skeleton" data-slot="sidebar-menu-skeleton" {...props} data-sentry-component="SidebarMenuSkeleton" data-sentry-source-file="sidebar.tsx">{t4}{t5}</div>;
    $[12] = props;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp() {
  return `${Math.floor(Math.random() * 40) + 50}%`;
}
function SidebarMenuSub(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5 group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ul className={t1} data-sidebar="menu-sub" data-slot="sidebar-menu-sub" {...props} data-sentry-component="SidebarMenuSub" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SidebarMenuSubButton(t0) {
  const $ = _c(15);
  let isActive;
  let render;
  let size;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      className,
      isActive: t4,
      render: t5,
      size: t6,
      ...props
    } = t0;
    render = t5;
    isActive = t4 === undefined ? false : t4;
    size = t6 === undefined ? "md" : t6;
    t3 = useRender;
    t1 = "a";
    t2 = mergeProps({
      className: cn("flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground ring-sidebar-ring outline-hidden group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-[size=md]:text-sm data-[size=sm]:text-xs [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground", className)
    }, props);
    $[0] = t0;
    $[1] = isActive;
    $[2] = render;
    $[3] = size;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    isActive = $[1];
    render = $[2];
    size = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isActive || $[8] !== size) {
    t4 = {
      active: isActive,
      sidebar: "menu-sub-button",
      size,
      slot: "sidebar-menu-sub-button"
    };
    $[7] = isActive;
    $[8] = size;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== render || $[11] !== t1 || $[12] !== t2 || $[13] !== t4) {
    t5 = {
      defaultTagName: t1,
      props: t2,
      render,
      state: t4
    };
    $[10] = render;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t3(t5);
}
function SidebarMenuSubItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-sub-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <li className={t1} data-sidebar="menu-sub-item" data-slot="sidebar-menu-sub-item" {...props} data-sentry-component="SidebarMenuSubItem" data-sentry-source-file="sidebar.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Sidebar, SidebarClose, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger };